import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from '@/App';
import '@/shared/styles/index.css';
import { NotificationProvider } from '@/app/providers/NotificationProvider';
import { ProcessingProvider } from '@/app/providers/ProcessingProvider';
import { UserProvider } from '@/app/providers/UserProvider';
import { WalletProvider } from '@/app/providers/WalletProvider';
import { redact } from '@/shared/utils/sentryRedact';

// User-submitted content (source text, prompts) must never reach Sentry via a stray
// console.log/thrown-error breadcrumb. See sentryRedact.ts for the redaction logic
// and its tests. (Session Replay already masks all text/inputs by default.)

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  const env = import.meta.env.MODE;
  let tracesSampleRate = 0.1;
  const tracesSampleRateEnv = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE;
  if (tracesSampleRateEnv) {
    tracesSampleRate = parseFloat(tracesSampleRateEnv);
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: env,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate,
    replaysSessionSampleRate: 0.0, // Session Replays off by default to conserve quota
    replaysOnErrorSampleRate: 1.0, // Capture replay if an error is thrown
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.message) event.message = redact(event.message) as string;
      if (event.extra) event.extra = redact(event.extra) as typeof event.extra;
      if (event.contexts) event.contexts = redact(event.contexts) as typeof event.contexts;
      if (event.request) event.request = redact(event.request) as typeof event.request;
      event.exception?.values?.forEach((exceptionValue) => {
        if (exceptionValue.value) {
          exceptionValue.value = redact(exceptionValue.value) as string;
        }
      });
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.message) breadcrumb.message = redact(breadcrumb.message) as string;
      if (breadcrumb.data) breadcrumb.data = redact(breadcrumb.data) as typeof breadcrumb.data;
      return breadcrumb;
    },
  });
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <NotificationProvider>
        <UserProvider>
          <WalletProvider>
            <ProcessingProvider>
              <App />
            </ProcessingProvider>
          </WalletProvider>
        </UserProvider>
      </NotificationProvider>
    </BrowserRouter>
  </React.StrictMode>,
);